import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { DatePicker } from '@interstate/components/DatePicker';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import type { DomEventType, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Spinner } from '@interstate/components/Spinner';
import { Dropdown, MaskTextInput, MaskDatePicker } from '@makemydeal/dr-shared-components';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { FieldsRow } from '../sectionLayout.styles';
import { LoadingIndicatorWrapper } from './spd.styles';
import { SPD_TEXT_MASK, SSN_PATTERN } from './spd.utils';
import type { CustomerSpdPresence, CustomerSpdValues } from '../../../types/customerSpd.types';

const PII_INPUT_ATTR = { textInput: { dataAttr: { className: 'pii' } } };

type SpdFieldsProps = {
    /** Which fields exist (drives which masked placeholders show). From CustomerSpd's derived updatedCheck. */
    updatedPresence?: CustomerSpdPresence;
    /** Saved presence from the CHECK response — the backend baseline, used to restore a cleared field. */
    presence?: CustomerSpdPresence;
    /** The working copy of values (edited/fetched). */
    values: CustomerSpdValues;
    /** True once the dealer clicked Show Data — fields are unmasked and editable. */
    showValues: boolean;
    /** True while the GET (show-data) fetch is in flight. */
    loading: boolean;
    /** Field-level change handler. */
    onChange: (field: keyof CustomerSpdValues, value: string) => void;
};

const toDate = (value?: string): Date | undefined => (value ? new Date(value) : undefined);

/**
 * Pure renderer for the SPD fields. Holds no state and performs no fetching — everything comes via
 * props and `isLocked` from CustomerFormContext. Masked (asterisks) until `showValues` is true.
 */
const SpdFields = ({ presence, updatedPresence, values, showValues, loading, onChange }: SpdFieldsProps) => {
    const { isLocked, customerRole, formErrors, spdBaseline } = useCustomerCardContext();
    const stateOptions = useMemo(getStateCodesSelectInputValues, StateCodes);
    const isConsolidatedSaveEnabled = useSelector(featureToggleSelectors.isCustomerCardSaveConsolidationEnabled);

    const testId = (name: string) => `customer-form-${customerRole}-${name}`;
    // Read at render time (not module scope) so jest.spyOn on featureToggleSelectors
    // still takes effect — module scope would capture the pre-spy reference once at import.
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    // SPD field errors from the validation engine (populated via CustomerSpd -> handleSpdValidate).
    const getError = (field: keyof CustomerSpdValues) => ({
        hasError: Boolean(formErrors?.[field]?.hasError),
        errorMessage: (formErrors?.[field]?.errorMessage ?? '') as string
    });

    // DL State is a non-sensitive code the CHECK response provides up-front (via `presence`). Show it
    // even before a GET, falling back to the check value when the dealer hasn't edited it — matches
    // dr-dash, where DL State is visible/pre-filled without fetching PII.
    const driversLicenseState = (values.driversLicenseState ?? updatedPresence?.driversLicenseState ?? '') as string;

    // DL State alone falls back to CHECK presence, which carries its real value (see note above).
    const savedValues: CustomerSpdValues = {
        ...spdBaseline,
        driversLicenseState: spdBaseline?.driversLicenseState ?? presence?.driversLicenseState ?? undefined
    };

    // Consolidated save sends every field, so restore the saved value instead of sending a blank.
    const handleOnBlur = (field: keyof CustomerSpdValues) => () => {
        if (!isConsolidatedSaveEnabled) return;
        const savedValue = savedValues[field];
        if (!values[field] && savedValue) onChange(field, savedValue);
    };

    const onTextChange = (field: keyof CustomerSpdValues) => (e: InterstateOnChangeEvent<string>) => {
        // TextInput also fires onChange on blur; ignore it so it can't overwrite what handleOnBlur restored.
        if ((e.domEvent as Exclude<DomEventType, Record<string, never>>)?.type === 'blur') return;
        onChange(field, e.target.value);
    };

    const onDateChange = (field: keyof CustomerSpdValues) => (e: InterstateOnChangeEvent<DatePickerEventValue>) =>
        onChange(field, (e.target.value?.dateValue as string) ?? '');

    // MaskTextInput types onChange as (field: string, value) — its `field` is always one of our SPD
    // field names, so adapt it to our narrower onChange signature.
    const onMaskChange = (field: string, value: string) => onChange(field as keyof CustomerSpdValues, value);

    if (loading) {
        return (
            <LoadingIndicatorWrapper data-testid="customer-spd-loading">
                <Spinner />
            </LoadingIndicatorWrapper>
        );
    }

    return (
        <FieldsRow>
            {showValues ? (
                <>
                    <TextInput
                        name="driversLicenseNumber"
                        displayLabel
                        label="Driver License Number"
                        size="small"
                        value={values.driversLicenseNumber ?? ''}
                        onChange={onTextChange('driversLicenseNumber')}
                        onBlur={handleOnBlur('driversLicenseNumber')}
                        disabled={isLocked}
                        hasError={getError('driversLicenseNumber').hasError}
                        errorMessage={getError('driversLicenseNumber').errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid={testId('driver-license-number')}
                        slotProps={PII_INPUT_ATTR}
                    />
                    <Dropdown
                        name="driversLicenseState"
                        displayLabel
                        label="DL State"
                        size="small"
                        options={stateOptions}
                        value={driversLicenseState}
                        onChange={(e) => onChange('driversLicenseState', String(e.target.value ?? ''))}
                        onBlur={handleOnBlur('driversLicenseState')}
                        allowRemoval
                        disabled={isLocked}
                        hasError={getError('driversLicenseState').hasError}
                        errorMessage={getError('driversLicenseState').errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid={testId('driver-license-state')}
                    />
                    <DatePicker
                        name="driversLicenseExpirationDate"
                        displayLabel
                        label="DL Expiration Date"
                        size="small"
                        value={toDate(values.driversLicenseExpirationDate)}
                        onChange={onDateChange('driversLicenseExpirationDate')}
                        onBlur={handleOnBlur('driversLicenseExpirationDate')}
                        disabled={isLocked}
                        hasError={getError('driversLicenseExpirationDate').hasError}
                        errorMessage={getError('driversLicenseExpirationDate').errorMessage}
                        data-testid={testId('drivers-license-expiration-date')}
                    />
                    <DatePicker
                        name="dateOfBirth"
                        displayLabel
                        label="Date of Birth"
                        size="small"
                        helpMessage="Age must be at least 18 years"
                        value={toDate(values.dateOfBirth)}
                        onChange={onDateChange('dateOfBirth')}
                        onBlur={handleOnBlur('dateOfBirth')}
                        disabled={isLocked}
                        hasError={getError('dateOfBirth').hasError}
                        errorMessage={getError('dateOfBirth').errorMessage}
                        data-testid={testId('date-of-birth')}
                    />
                    <TextInput
                        name="socialSecurityNumber"
                        displayLabel
                        label="Social Security Number"
                        size="small"
                        masking={SSN_PATTERN}
                        value={values.socialSecurityNumber ?? ''}
                        onChange={onTextChange('socialSecurityNumber')}
                        onBlur={handleOnBlur('socialSecurityNumber')}
                        disabled={isLocked}
                        hasError={getError('socialSecurityNumber').hasError}
                        errorMessage={getError('socialSecurityNumber').errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid={testId('social-security-number')}
                        slotProps={PII_INPUT_ATTR}
                    />
                </>
            ) : (
                <>
                    <MaskTextInput
                        name="driversLicenseNumber"
                        label="Driver License Number"
                        size="small"
                        maskedValue={SPD_TEXT_MASK}
                        defaultMask={!!updatedPresence?.driversLicenseNumber}
                        value={values.driversLicenseNumber}
                        onChange={onMaskChange}
                        disabled={isLocked}
                        hasError={getError('driversLicenseNumber').hasError}
                        errorMessage={getError('driversLicenseNumber').errorMessage}
                        data-testid={testId('driver-license-number')}
                    />
                    <Dropdown
                        name="driversLicenseState"
                        displayLabel
                        label="DL State"
                        size="small"
                        options={stateOptions}
                        value={driversLicenseState}
                        onChange={(e) => onChange('driversLicenseState', String(e.target.value ?? ''))}
                        onBlur={handleOnBlur('driversLicenseState')}
                        allowRemoval
                        disabled={isLocked}
                        hasError={getError('driversLicenseState').hasError}
                        errorMessage={getError('driversLicenseState').errorMessage}
                        selectOnFocusSelector={selectOnFocusSelector}
                        data-testid={testId('driver-license-state')}
                    />
                    <MaskDatePicker
                        name="driversLicenseExpirationDate"
                        label="DL Expiration Date"
                        size="small"
                        defaultMask={!!updatedPresence?.driversLicenseExpirationDate}
                        value={toDate(values.driversLicenseExpirationDate)}
                        handleDatePickerOnChange={(field, e) =>
                            onChange(field as keyof CustomerSpdValues, (e.target.value?.dateValue as string) ?? '')
                        }
                        disabled={isLocked}
                        hasError={getError('driversLicenseExpirationDate').hasError}
                        errorMessage={getError('driversLicenseExpirationDate').errorMessage}
                        data-testid={testId('drivers-license-expiration-date')}
                    />
                    <MaskDatePicker
                        name="dateOfBirth"
                        label="Date of Birth"
                        size="small"
                        helpMessage="Age must be at least 18 years"
                        defaultMask={!!updatedPresence?.dateOfBirth}
                        value={toDate(values.dateOfBirth)}
                        handleDatePickerOnChange={(field, e) =>
                            onChange(field as keyof CustomerSpdValues, (e.target.value?.dateValue as string) ?? '')
                        }
                        disabled={isLocked}
                        hasError={getError('dateOfBirth').hasError}
                        errorMessage={getError('dateOfBirth').errorMessage}
                        data-testid={testId('date-of-birth')}
                    />
                    <MaskTextInput
                        name="socialSecurityNumber"
                        label="Social Security Number"
                        size="small"
                        maskedValue={SPD_TEXT_MASK}
                        masking={SSN_PATTERN}
                        defaultMask={!!updatedPresence?.socialSecurityNumber}
                        value={values.socialSecurityNumber}
                        onChange={onMaskChange}
                        disabled={isLocked}
                        hasError={getError('socialSecurityNumber').hasError}
                        errorMessage={getError('socialSecurityNumber').errorMessage}
                        data-testid={testId('social-security-number')}
                    />
                </>
            )}
        </FieldsRow>
    );
};

export default SpdFields;
